// MUI
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SupportAgent from '@mui/icons-material/SupportAgent';
import BackIcon from '@mui/icons-material/ArrowBackIos';

// Redux
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createChatStart, getAllChatsStart, getMyChatsStart, setCurrentChat } from '../../redux/actions/chatActions';
import { ApplicationState } from '../../interface/ApplicationState';

// Custom
import { PastHelpRequests } from './PastHelpRequests';
import { ConversationView } from './ConversationView';
import { MESSAGE_POLL_INTERVAL } from '../../const';
import Dialog from '@mui/material/Dialog';
import { Transition } from '../../animations/Transition';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { alpha, useTheme } from '@mui/material/styles';

interface ChatWindowProps
{
  onClose: () => void
}

let pollId: any

export const ChatWindow = ({ onClose }: ChatWindowProps) =>
{
  const theme = useTheme()

  const dispatch = useDispatch()
  const conversations = useSelector((state: ApplicationState) => state.chat.conversations)
  const currentChat = useSelector((state: ApplicationState) => state.chat.currentChat)
  const isCreatingChat = useSelector((state: ApplicationState) => state.chat.isCreatingChat)

  const user = useSelector((state: ApplicationState) => state.userStore.user)

  const [ subject, setSubject ] = useState("")

  useEffect(() => {

    getMessages()
    pollForMessages()

    return function()
    {
      clearInterval(pollId)
    }

  }, [])

  const getMessages = () =>
  {
    if(!user.isAdmin) dispatch(getMyChatsStart())
    if(user.isAdmin) dispatch(getAllChatsStart())
  }

  const pollForMessages = () =>
  {
    pollId = setInterval(() => {

      getMessages()

    }, MESSAGE_POLL_INTERVAL)

  }

  const getCurrentChat = () =>
  {
    if(currentChat) return conversations.find(c => c._id === currentChat)?.subject
    return "AlgoTrader Support Chat"
  }

  const getUser = () =>
  {
    if(currentChat) return conversations.find(c => c._id === currentChat)?.email
    return ""
  }

  const createChat = () =>
  {
    if(subject)
    {
      dispatch(createChatStart(subject))
      setSubject("")
    }
  }

  return(<Dialog
    open
    onClose={onClose}
    TransitionComponent={Transition}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: styles.dialog(theme) }}>


    {/* Header View */}
    <Box sx={styles.header(theme)}>
      { currentChat && <IconButton onClick={() => dispatch(setCurrentChat(""))}><BackIcon /></IconButton> }

      <Box flex={1}>
        <Typography flex={1} textAlign='center' alignSelf="center" fontWeight="700" variant="subtitle1" >{getCurrentChat()}</Typography>
        <Typography flex={1} textAlign='center' alignSelf="center" fontWeight="600" variant="body2" color="textSecondary">{getUser()}</Typography>
      </Box>


      <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

    {/* Body */}
    <Box sx={styles.container}>
      { !currentChat && <PastHelpRequests conversations={conversations} isAdmin={user.isAdmin} /> }
      { currentChat && <ConversationView conversationId={currentChat} /> }
    </Box>

    {/* Init chat section - only for normal users  */}
     { !currentChat && !user.isAdmin && <Box sx={styles.startConvoSection(theme)}>
        <TextField
          autoFocus
          value={subject}
          placeholder="Subject or problem description (E.G) API key issues"
          sx={styles.subject(theme)}
          onChange={(e: any) => setSubject(e.target.value)}
        />
        <Button variant="contained" sx={styles.primaryButton} startIcon={isCreatingChat ? <CircularProgress size={16} /> : <SupportAgent />} onClick={createChat} disabled={!subject || isCreatingChat}>Start new help request</Button>
      </Box> }
        
   </Dialog>)
}

export const styles = {
  dialog: (theme: any) => ({
    borderRadius: "18px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  header: (theme: any) => ({
    display: 'flex' as 'flex',
    flexDirection: "row" as "row",
    alignItems: 'center' as 'center',
    paddingTop: 1,
    paddingLeft: 1,
    paddingRight: 1,
    paddingBottom: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  container: {
    zIndex: 1,
    minHeight: 520,
    padding: 1,
  },
  subject: (theme: any) => ({
    marginBottom: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: "14px",
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.76),
    },
  }),
  startConvoSection: (theme: any) => ({
    margin: 2,
    display: 'flex' as 'flex',
    justifyContent: 'center' as 'center',
    flexDirection: "column" as "column",
    paddingTop: 8,
    borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
  }),
  list: {
    overflow: "auto",
    height: 430
  },
  textInput: {
  },
  sending: {
    marginTop: 1,
    borderRadius: 2,
    marginLeft: 2,
    marginRight: 2,
  },
  primaryButton: {
    borderRadius: "12px",
    boxShadow: "none",
  },
}
